package fr.frux.core.designsystem.components

import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.add
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.NavigationRail
import androidx.compose.material3.NavigationRailDefaults
import androidx.compose.material3.NavigationRailItem
import androidx.compose.material3.NavigationRailItemDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.theme.FruxSpacing

/**
 * Rail de navigation grand écran (spec 2026-09-10, §4) : la barre du bas [FruxBottomNav] vue de
 * côté. Mêmes destinations, même pastille « Ajouter » ([AddTreeButton]), placée en BAS du rail
 * (amendement du 2026-09-11) : en tête, elle était à l'opposé des actions de la carte, qui vivent
 * en bas à droite. Aucun `header` M3 : le slot header est en haut par construction.
 */
@Composable
fun <T> FruxNavigationRail(
    items: List<FruxNavItem<T>>,
    isSelected: (FruxNavItem<T>) -> Boolean,
    onSelect: (FruxNavItem<T>) -> Unit,
    onAddClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    NavigationRail(
        modifier = modifier,
        containerColor = MaterialTheme.colorScheme.surface,
        // Inset plutôt que Spacer en tête : le centre de la pastille du premier onglet tombe sur
        // celui de la barre de recherche de la carte (4 dp de rail M3 + 4 dp ici + 8 dp de
        // centrage dans [RailItemHeight] = pastille de 32 dp centrée à 32 dp sous la barre
        // d'état, comme la recherche, haute de 44 dp à 10 dp du bord).
        windowInsets = NavigationRailDefaults.windowInsets.add(WindowInsets(top = FruxSpacing.xs)),
    ) {
        items.forEach { item ->
            NavigationRailItem(
                selected = isSelected(item),
                onClick = { onSelect(item) },
                modifier = Modifier.heightIn(min = RailItemHeight),
                icon = { Icon(item.icon, contentDescription = null) },
                label = { Text(item.label, style = MaterialTheme.typography.labelMedium) },
                colors = NavigationRailItemDefaults.colors(indicatorColor = MaterialTheme.colorScheme.primaryContainer),
            )
        }
        RailFooter(onAddClick)
    }
}

/** Pousse la pastille en bas du rail : le contenu de [NavigationRail] est une `ColumnScope`. */
@Composable
private fun ColumnScope.RailFooter(onAddClick: () -> Unit) {
    Spacer(Modifier.weight(1f))
    AddTreeButton(
        onClick = onAddClick,
        modifier = Modifier.padding(bottom = FruxSpacing.md),
    )
}

/**
 * Hauteur d'un onglet du rail. Le minimum M3 (56 dp) empile icône et libellé sans respiration,
 * ce qui donnait un rail écrasé à côté de la barre du bas (80 dp). Alignée sur cette dernière.
 */
private val RailItemHeight = 72.dp
